import { getReferenceString } from "@medplum/core";
import type { Coding } from "@medplum/fhirtypes";
import {
  memberKey,
  ORDER_PARAMETER_VALUE_URL,
  syncKey,
  TREATMENT_CHOICE_CODE_SYSTEM,
  TREATMENT_TRIAL_PARAMETER_SYSTEM,
  unitToQuantity,
} from "@repo/api";
import type { RecordProcedureTrialInput } from "@repo/api/schemas";
import type { AnsweredValue } from "@repo/domain";
import { TRIAL_PLANNED_TRIAL_URL } from "@repo/fhir-systems/extensions";
import { PATIENT_DOCUMENT_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import type { Collection } from "@tanstack/db";
import { orderJoinKeys } from "../fhir/collections";
import { ORDER_REVOKED_AT_URL } from "../fhir/order-set-storage";
import type { ObservationRow, ProcedureRow, RequestGroupRow, ServiceRequestRow } from "../fhir/types";

export type ProcedureIdentity = { orderSetId: string; orderId: string };
type MutableRows<T extends ProcedureRow | RequestGroupRow | ServiceRequestRow | ObservationRow> = Pick<
  Collection<T, string>,
  "state" | "insert" | "update"
>;

export type KeyedProcedure = ProcedureRow & { domainId: string };

export function procedureFor(procedures: MutableRows<ProcedureRow>, input: ProcedureIdentity) {
  const row = [...procedures.state.values()].find(
    (candidate) => candidate.orderSetJoinKey === input.orderSetId && candidate.orderJoinKey === input.orderId,
  );
  return row?.domainId ? (row as KeyedProcedure) : undefined;
}

export function beginProcedure(
  procedures: MutableRows<ProcedureRow>,
  input: ProcedureIdentity,
  create: () => ProcedureRow,
  resume = true,
): KeyedProcedure {
  const existing = procedureFor(procedures, input);
  if (existing) {
    if (resume && existing.status === "entered-in-error") {
      procedures.update(syncKey(existing), (draft) => {
        draft.status = "in-progress";
        draft.performedPeriod = { start: new Date().toISOString() };
      });
      return procedures.state.get(syncKey(existing)) as KeyedProcedure;
    }
    return existing;
  }
  const created = create();
  if (!created.domainId) throw new Error("An optimistic procedure requires an identity");
  procedures.insert(created);
  return created as KeyedProcedure;
}

export function updateProcedureOrder(
  deps: { serviceRequests: () => MutableRows<ServiceRequestRow>; requestGroups: () => MutableRows<RequestGroupRow> },
  input: ProcedureIdentity,
  status: "draft" | "completed" | "revoked",
) {
  const group = deps.requestGroups().state.get(input.orderSetId);
  if (!group) throw new Error(`OrderSet '${input.orderSetId}' is not loaded`);
  const key = memberKey(input.orderSetId, input.orderId);
  const order = deps.serviceRequests().state.get(key);
  if (!order) throw new Error(`Order '${input.orderId}' is not loaded`);
  deps.serviceRequests().update(key, (draft) => {
    draft.status = status;
    if (status !== "completed") {
      const extensions = (order.extension ?? []).filter((extension) => extension.url !== ORDER_REVOKED_AT_URL);
      draft.extension =
        status === "revoked"
          ? [...extensions, { url: ORDER_REVOKED_AT_URL, valueDateTime: new Date().toISOString() }]
          : extensions;
    }
  });
  if (status === "draft" && group.status === "completed") {
    deps.requestGroups().update(input.orderSetId, (draft) => {
      draft.status = "active";
    });
  }
}

/** A child of the procedure points at it by identity; the locator rides along once the server has one. */
export function procedureReference(procedure: KeyedProcedure, system: string) {
  return {
    ...(procedure.id ? { reference: getReferenceString(procedure) } : {}),
    identifier: { system, value: procedure.domainId },
  };
}

export function valueToFhir(value: AnsweredValue) {
  switch (value.kind) {
    case "Quantity":
      return { valueQuantity: { value: value.value, ...unitToQuantity(value.unit) } };
    case "Integer":
      return { valueInteger: value.value };
    case "Boolean":
      return { valueBoolean: value.value };
    case "Choice":
      return {
        valueCodeableConcept: {
          coding: value.selected.map((code) => ({ system: TREATMENT_CHOICE_CODE_SYSTEM, code: String(code) })),
          ...(value.freeText ? { text: value.freeText } : {}),
        },
      };
    case "Text":
      return { valueString: value.value };
  }
}

export type TrialRowKind = { idSystem: string; procedureSystem: string; sourceUrl: string; tag: Coding };

export function trialRowFor(
  observations: MutableRows<ObservationRow>,
  procedure: KeyedProcedure,
  kind: TrialRowKind,
  trialId: string,
) {
  const row = observations.state.get(memberKey(procedure.domainId, trialId));
  return row?.identifier?.some((identifier) => identifier.system === kind.idSystem) ? row : undefined;
}

export function writeTrialRow(
  observations: MutableRows<ObservationRow>,
  procedure: KeyedProcedure,
  kind: TrialRowKind,
  input: Pick<RecordProcedureTrialInput, "id" | "effectiveDateTime" | "values"> & {
    plannedTrialId?: string | null;
    derivedFromDocumentId?: string;
  },
  corrected: boolean,
) {
  const fields = {
    status: (corrected ? "corrected" : "final") as ObservationRow["status"],
    effectiveDateTime: input.effectiveDateTime ?? new Date().toISOString(),
    component: input.values.map(({ answer, source }) => ({
      code: { coding: [{ system: TREATMENT_TRIAL_PARAMETER_SYSTEM, code: answer.parameterId }] },
      ...valueToFhir(answer.value),
      extension: [
        {
          url: ORDER_PARAMETER_VALUE_URL,
          extension: [
            { url: "source", valueString: answer.source },
            { url: "parameterId", valueString: answer.parameterId },
            { url: "kind", valueCode: answer.value.kind },
          ],
        },
        { url: kind.sourceUrl, valueCode: source },
      ],
    })),
  };
  const key = memberKey(procedure.domainId, input.id);
  if (trialRowFor(observations, procedure, kind, input.id)) {
    observations.update(key, (draft) => {
      Object.assign(draft, fields);
      if (input.plannedTrialId !== undefined)
        draft.extension = [
          ...(draft.extension ?? []).filter((entry) => entry.url !== TRIAL_PLANNED_TRIAL_URL),
          ...(input.plannedTrialId ? [{ url: TRIAL_PLANNED_TRIAL_URL, valueString: input.plannedTrialId }] : []),
        ];
    });
    return;
  }
  observations.insert({
    resourceType: "Observation",
    identifier: [{ system: kind.idSystem, value: input.id }],
    meta: { tag: [kind.tag] },
    code: { coding: [{ system: TREATMENT_TRIAL_PARAMETER_SYSTEM, code: kind.tag.code }] },
    ...fields,
    ...(input.plannedTrialId
      ? { extension: [{ url: TRIAL_PLANNED_TRIAL_URL, valueString: input.plannedTrialId }] }
      : {}),
    ...(input.derivedFromDocumentId
      ? {
          derivedFrom: [
            {
              type: "DocumentReference" as const,
              identifier: { system: PATIENT_DOCUMENT_ENTITY_ID_SYSTEM, value: input.derivedFromDocumentId },
            },
          ],
        }
      : {}),
    subject: procedure.subject as ObservationRow["subject"],
    ...(procedure.encounter ? { encounter: procedure.encounter } : {}),
    partOf: [procedureReference(procedure, kind.procedureSystem)],
    procedureId: procedure.domainId,
    procedureJoinKey: procedure.domainId,
    ...orderJoinKeys(key, undefined, undefined),
  });
}

export function retractTrialReadings(
  observations: MutableRows<ObservationRow>,
  procedureId: string,
  trialSystem: string,
  trialId: string,
): void {
  for (const row of observations.state.values()) {
    if (
      row.procedureJoinKey !== procedureId ||
      !row.derivedFrom?.some(
        (reference) => reference.identifier?.system === trialSystem && reference.identifier.value === trialId,
      )
    )
      continue;
    observations.update(syncKey(row), (draft) => {
      draft.status = "entered-in-error";
    });
  }
}
